import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AddProjectToWorkspace from './AddProjectToWorkspace';
import { MinusCircleIcon } from '@phosphor-icons/react';
import { ColumnsMenu, ProjectFilters, useHiddenColumns, useProjectListView } from './ProjectListTools';
import DataTable from '../table/DataTable';
import { IconButton } from '../table/TableParts';
import RunConfirm from './RunConfirm';
import QualityRunStatus, { RUN_DETAIL } from './QualityRunStatus';
import useQualityRuns, { qualityPlan } from './useQualityRuns';
import ShareDialog from '../ShareDialog';
import { ago } from '../../utils/blameModel';
import { removeProjectFromWorkspace } from '../../services/WorkspacesService';
import { sleepSandbox } from '../../services/SandboxService';
import {
  bulkEligibility, buildRows, lastChanges, reportText,
} from '../../utils/workspacePageModel';

const COLUMNS = [
  { key: 'name', label: 'Project' },
  { key: 'state', label: 'State' },
  { key: 'problems', label: 'Problems' },
  { key: 'quality', label: 'Quality' },
  { key: 'owner', label: 'Owner' },
  { key: 'changed', label: 'Last change' },
];
const ACTIONS = [
  { id: 'start', label: 'Start selected', tone: 'is-go' },
  { id: 'stop', label: 'Stop selected', tone: 'is-stop' },
  { id: 'sleep', label: 'Put to sleep', tone: '' },
  { id: 'quality', label: 'Run quality', tone: '' },
  { id: 'share', label: 'Share', tone: '' },
  { id: 'remove', label: 'Remove from this workspace', tone: '' },
];
const DETAIL = {
  quality: RUN_DETAIL,
  sleep: 'Your sandbox sleeps as a whole: every app you run stops, also the ones outside this selection. It wakes when you open a project.',
  share: 'You choose the people for each one, one after the other.',
  remove: 'The projects are kept, with their owners and the people they are shared with directly. They leave this workspace only: their other workspaces keep them.',
};

// Columns ▾: which columns show, remembered per browser (the Project column always shows).
const COLUMNS_KEY = 'flow.workspaceProjects.hiddenColumns';

/**
 * The workspace's Projects tab (design "Flow Workspace", Projects): a sortable table (state, problems, quality, owner, last
 * change), a search and filters (State, Owner: Me / Others / a person, Has problems; ProjectListTools.jsx, shared with
 * the Overview's Projects gadget), the "Add project" select, and row selection with bulk actions: Start selected, Stop
 * selected, Put to sleep, Run quality, Share, Remove from this workspace (its other workspaces keep it), and Columns ▾
 * to show or hide columns (remembered in this browser). Each asks first and acts only on the projects the person may
 * act on; the others are skipped and named, with why (utils/workspacePageModel.js bulkEligibility).
 */
export default function WorkspaceProjects({
  workspace, userSub, canEdit, mayRemove, runStates, quality, activity, run, projectRun, onOpenProject, onChanged, onQualityReload = null,
}) {
  const [selected, setSelected] = useState([]);
  const [pending, setPending] = useState(null);
  const [sharing, setSharing] = useState([]); // the projects waiting for their Share dialog, the first is open
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const { show, toggle: toggleColumn } = useHiddenColumns(COLUMNS_KEY, COLUMNS);
  const checks = useQualityRuns({ onFinished: () => onQualityReload?.() });

  const { projects } = workspace;
  const changes = useMemo(() => lastChanges(Object.fromEntries(Object.entries(activity).map(([id, entry]) => [id, entry?.value]))), [activity]);
  const all = useMemo(() => buildRows(projects, { answers: runStates, quality, changes, userSub }), [projects, runStates, quality, changes, userSub]);
  const { filters, setFilter, sort, sortBy, rows } = useProjectListView(all);
  const shownIds = rows.map((row) => row.projectId);
  const chosen = selected.filter((id) => projects.some((project) => project.projectId === id));
  const allShown = shownIds.length > 0 && shownIds.every((id) => chosen.includes(id));

  const toggle = (id) => setSelected((now) => (now.includes(id) ? now.filter((x) => x !== id) : [...now, id]));
  const toggleAll = () => setSelected(allShown ? chosen.filter((id) => !shownIds.includes(id)) : [...new Set([...chosen, ...shownIds])]);

  const picked = projects.filter((project) => chosen.includes(project.projectId));
  // `only`: the projects to act on (a row's own Remove), else the selected ones.
  const ask = async(action, only = picked) => {
    setError(null);
    run.clearReport();
    setWorking(true);
    try {
      let plan;
      if (action === 'start' || action === 'stop') { plan = await run.prepare(action, only); } else if (action === 'quality') { plan = qualityPlan(only); } else {
        plan = bulkEligibility(action, only, { workspace, userSub });
      }
      setPending({ action, plan, detail: DETAIL[action] });
    } catch (failed) {
      setError(failed.message);
    } finally {
      setWorking(false);
    }
  };

  const confirm = async() => {
    const { action, plan } = pending;
    setPending(null);
    setSelected([]);
    if (action === 'start' || action === 'stop') {
      await run.execute(action, plan);
      return;
    }
    if (action === 'share') {
      setSharing(plan.eligible);
      return;
    }
    if (action === 'quality') {
      checks.clear();
      checks.start(plan.eligible);
      return;
    }
    setWorking(true);
    const done = [];
    const failed = [];
    try {
      if (action === 'sleep') {
        await sleepSandbox();
        projectRun.refreshAll();
        run.setReport({ text: reportText({ verb: 'Put to sleep', done: plan.eligible.map((entry) => entry.name), skipped: plan.skipped }), tone: 'ok' });
        return;
      }
      for (const entry of plan.eligible) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await removeProjectFromWorkspace(workspace.id, entry.projectId);
          done.push(entry.name);
        } catch (failure) {
          failed.push({ name: entry.name, error: failure.message });
        }
      }
      run.setReport({ text: reportText({ verb: 'Removed', done, skipped: plan.skipped, failed }), tone: failed.length ? 'error' : 'ok' });
      await onChanged();
    } catch (failure) {
      run.setReport({ text: failure.message, tone: 'error' });
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="workspace-section ws-projects">
      {canEdit && <AddProjectToWorkspace onAdded={onChanged} workspace={workspace} />}
      <div className="ws-tools">
        <ProjectFilters all={all} filters={filters} setFilter={setFilter} />
        <ColumnsMenu columns={COLUMNS} show={show} toggle={toggleColumn} />
      </div>
      <QualityRunStatus checks={checks} />
      {error && <p className="sbx-action-error" role="alert">{error}</p>}
      {chosen.length > 0 && (
        <div aria-label="Actions on the selected projects" className="ws-bulk" role="toolbar">
          <strong>{chosen.length} selected</strong>
          <span className="ws-muted ws-grow">Actions apply to the selection only</span>
          {ACTIONS.map((action) => (
            <button className={`ws-btn ${action.tone}`} disabled={working || run.busy || (action.id === 'quality' && checks.active)} key={action.id} onClick={() => ask(action.id)} type="button">{action.label}</button>
          ))}
        </div>
      )}
      {projects.length === 0 ? (
        <p className="workspaces-note">No projects in this workspace yet.</p>
      ) : (
        <DataTable
          actions={{
            label: 'Actions',
            width: 110,
            render: (row) => (
              <span className="ws-cell-actions">
                <button aria-label={`Open ${row.name}`} className="ws-btn" onClick={() => onOpenProject(row.projectId)} type="button">Open</button>
                {mayRemove(row.project) && (
                  <IconButton
                    className="ws-btn is-icon"
                    disabled={working}
                    icon={<MinusCircleIcon aria-hidden size={15} />}
                    label={`Remove ${row.name} from this workspace`}
                    onClick={() => ask('remove', [row.project])}
                    tip="Remove from this workspace (its other workspaces keep it)"
                  />
                )}
              </span>
            ),
          }}
          ariaLabel="Projects in this workspace"
          className="ws-table"
          columnState={{ show, toggle: toggleColumn }}
          columns={[
            {
              key: 'name',
              label: 'Project',
              minWidth: 140,
              value: (row) => row.name,
              title: (row) => row.name,
              render: (row) => (
                <>
                  <button className="ws-name" onClick={() => onOpenProject(row.projectId)} type="button"><strong>{row.name}</strong></button>
                  <div className="ws-muted dt-line">{row.services === null ? '' : `${row.services} service${row.services === 1 ? '' : 's'}`}</div>
                </>
              ),
            },
            { key: 'state', label: 'State', width: 120, value: (row) => row.state.label, render: (row) => <span className={`ws-chip is-${row.state.tone}`}>{row.state.label}</span> },
            { key: 'problems', label: 'Problems', width: 140, value: (row) => row.problems.text, render: (row) => (row.problems.count ? <span className="ws-chip is-bad">{row.problems.text}</span> : row.problems.text) },
            {
              key: 'quality',
              label: 'Quality',
              width: 150,
              value: (row) => row.quality.text,
              render: (row) => (
                <>
                  <span className={`ws-chip is-${row.quality.tone}`}>{row.quality.text}</span>
                  {checks.runs[row.projectId]?.status === 'running' && <> <span className="ws-chip is-info">Checking…</span></>}
                </>
              ),
            },
            { key: 'owner', label: 'Owner', width: 140, value: (row) => row.owner },
            {
              key: 'changed',
              label: 'Last change',
              width: 110,
              value: (row) => row.lastChange,
              title: (row) => (row.lastChange === null ? undefined : new Date(row.lastChange).toLocaleString()),
              render: (row) => (row.lastChange === null ? '—' : <time dateTime={new Date(row.lastChange).toISOString()}>{ago(new Date(row.lastChange).toISOString())}</time>),
            },
          ]}
          empty={<span className="ws-muted">No project matches.</span>}
          leading={{
            width: 44,
            header: <label className="ws-check"><input aria-label="Select all projects" checked={allShown} onChange={toggleAll} type="checkbox" /></label>,
            render: (row) => <label className="ws-check"><input aria-label={`Select ${row.name}`} checked={chosen.includes(row.projectId)} onChange={() => toggle(row.projectId)} type="checkbox" /></label>,
          }}
          onSort={(next) => sortBy(next.key)}
          rowKey="projectId"
          rowProps={(row) => ({ className: chosen.includes(row.projectId) ? 'is-selected' : undefined })}
          rows={rows}
          scrollLabel="Projects table, scrolls sideways"
        serverSort
          sort={sort}
        />
      )}
      <RunConfirm onCancel={() => setPending(null)} onConfirm={confirm} pending={pending} />
      {sharing.length > 0 && (
        <ShareDialog
          key={sharing[0].projectId}
          onClose={() => setSharing((now) => now.slice(1))}
          projectId={sharing[0].projectId}
          projectName={sharing[0].name}
        />
      )}
    </div>
  );
}

WorkspaceProjects.propTypes = {
  activity: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  mayRemove: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  onOpenProject: PropTypes.func.isRequired,
  onQualityReload: PropTypes.func,
  projectRun: PropTypes.object.isRequired,
  quality: PropTypes.object.isRequired,
  run: PropTypes.object.isRequired,
  runStates: PropTypes.object.isRequired,
  userSub: PropTypes.string,
  workspace: PropTypes.object.isRequired,
};
